import React, { useState, useEffect } from "react";
import { FaBell, FaTimes, FaShieldAlt } from "react-icons/fa";
import { requestWebPushToken } from "../firebase";
import API_BASE from "../config/api";

const NotificationSettingsModal = ({ isOpen, onClose }) => {
  const [permission, setPermission] = useState("default");
  const [token, setToken] = useState(localStorage.getItem("gj_fcm_token") || "");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [preferences, setPreferences] = useState({
    rateAlerts: true,
    promotions: false, // Strictly default false (explicit opt-in required)
    orderUpdates: true,
  });

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      setPermission(Notification.permission);
    }
  }, [isOpen]);

  const handleEnablePush = async () => {
    setLoading(true);
    setStatusMessage("");

    try {
      const res = await requestWebPushToken();

      if (res.success && res.token) {
        setToken(res.token);
        localStorage.setItem("gj_fcm_token", res.token);
        setPermission("granted");

        // Sync with backend
        const user = JSON.parse(localStorage.getItem("user") || "null");
        await fetch(`${API_BASE}/api/notifications/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: res.token,
            platform: "web",
            preferences,
            userId: user?._id || user?.id || null,
            deviceInfo: {
              userAgent: navigator.userAgent,
            },
          }),
        });

        setStatusMessage("✅ Notifications successfully enabled!");
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        if (res.reason === "permission_denied") {
          setPermission("denied");
          setStatusMessage("❌ Notification permission was blocked in your browser.");
        } else {
          setStatusMessage("⚠️ Could not register notifications on this browser.");
        }
      }
    } catch (err) {
      setStatusMessage("❌ Failed to enable notifications.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePreferences = async (updated) => {
    setPreferences(updated);
    if (!token) return;

    try {
      await fetch(`${API_BASE}/api/notifications/preferences/${encodeURIComponent(token)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      });
    } catch (err) {
      console.error("Failed to update preferences:", err);
    }
  };

  const handleUnsubscribe = async () => {
    if (!token) return;
    setLoading(true);

    try {
      await fetch(`${API_BASE}/api/notifications/unregister`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      localStorage.removeItem("gj_fcm_token");
      setToken("");
      setStatusMessage("You have been unsubscribed from push notifications.");
    } catch (err) {
      setStatusMessage("Failed to unsubscribe.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.75)",
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #11141a 0%, #1c2330 100%)",
          border: "1.5px solid #f5d76e",
          borderRadius: "16px",
          maxWidth: "460px",
          width: "100%",
          padding: "24px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.8), 0 0 25px rgba(245,215,110,0.2)",
          color: "#fff",
          position: "relative",
          fontFamily: "inherit",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "none",
            border: "none",
            color: "#94a3b8",
            fontSize: "1.2rem",
            cursor: "pointer",
          }}
        >
          <FaTimes />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <div
            style={{
              background: "rgba(245, 215, 110, 0.15)",
              border: "1px solid #f5d76e",
              borderRadius: "12px",
              padding: "10px",
              color: "#f5d76e",
              fontSize: "1.3rem",
            }}
          >
            <FaBell />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#f5d76e" }}>
              GJ Bullion Notifications
            </h3>
            <p style={{ margin: "2px 0 0 0", fontSize: "0.82rem", color: "#94a3b8" }}>
              Instant market rates & genuine trading price alerts
            </p>
          </div>
        </div>

        {permission === "denied" ? (
          <div
            style={{
              background: "rgba(239, 68, 68, 0.15)",
              border: "1px solid #ef4444",
              borderRadius: "10px",
              padding: "12px",
              marginBottom: "16px",
              fontSize: "0.85rem",
              color: "#fca5a5",
            }}
          >
            Notifications are blocked in your browser settings. To receive live Indore bullion rate alerts, please enable notifications in your browser address bar.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                background: "rgba(255,255,255,0.04)",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <input
                type="checkbox"
                checked={preferences.rateAlerts}
                onChange={(e) =>
                  handleUpdatePreferences({ ...preferences, rateAlerts: e.target.checked })
                }
                style={{ width: "16px", height: "16px", accentColor: "#f5d76e" }}
              />
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>Gold & Silver Rate Alerts</div>
                <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                  Opening rates & genuine ±₹500 market movements
                </div>
              </div>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                background: "rgba(255,255,255,0.04)",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <input
                type="checkbox"
                checked={preferences.promotions}
                onChange={(e) =>
                  handleUpdatePreferences({ ...preferences, promotions: e.target.checked })
                }
                style={{ width: "16px", height: "16px", accentColor: "#f5d76e" }}
              />
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>Festival & Promotional Offers</div>
                <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                  Seasonal discounts and bullion special announcements
                </div>
              </div>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                background: "rgba(255,255,255,0.04)",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <input
                type="checkbox"
                checked={preferences.orderUpdates}
                onChange={(e) =>
                  handleUpdatePreferences({ ...preferences, orderUpdates: e.target.checked })
                }
                style={{ width: "16px", height: "16px", accentColor: "#f5d76e" }}
              />
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>Trade & Booking Updates</div>
                <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                  Verification and status alerts for your orders
                </div>
              </div>
            </label>
          </div>
        )}

        {statusMessage && (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: "8px",
              fontSize: "0.82rem",
              marginBottom: "14px",
              background: statusMessage.startsWith("✅") ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)",
              color: statusMessage.startsWith("✅") ? "#86efac" : "#fca5a5",
            }}
          >
            {statusMessage}
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", alignItems: "center" }}>
          {token ? (
            <button
              onClick={handleUnsubscribe}
              disabled={loading}
              style={{
                background: "transparent",
                color: "#ef4444",
                border: "1px solid rgba(239,68,68,0.4)",
                padding: "8px 14px",
                borderRadius: "8px",
                fontSize: "0.82rem",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Unsubscribe
            </button>
          ) : null}

          <button
            onClick={token ? onClose : handleEnablePush}
            disabled={loading}
            style={{
              background: "#f5d76e",
              color: "#000",
              border: "none",
              padding: "9px 20px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 2px 10px rgba(245,215,110,0.3)",
            }}
          >
            {loading ? "Processing..." : token ? "Save & Close" : "Enable Rate Alerts"}
          </button>
        </div>

        <div
          style={{
            marginTop: "16px",
            paddingTop: "12px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#64748b",
            fontSize: "0.72rem",
          }}
        >
          <FaShieldAlt /> No spam guarantee. Maximum 3 rate alerts per day. Unsubscribe anytime.
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
